import { Button, Grid, TextField } from '@mui/material';
import { FormAutoCompleteSelectDynamic } from '../../components/hook-form/form-autocomplete-select.dynamic.component';
import { customersApiKeyStore } from '../../api/customers-api-key-store';
import { FormAutoCompleteMultiSelectDynamic } from '../../components/hook-form/form-autocomplete-multi-select.dynamic.component';
import { officeApiKeyStore } from '../../api/office-api-key-store';
import { projectsApiKeyStore } from '../../api/projects-api-key-store';
import { marketAreaApiKeyStore } from '../../api/masters/market-area-api-key-store';
import { usersApiKeyStore } from '../../api/users-api-key-store';
import { workOrdersApiKeyStore } from '../../api/workorders-api-key-store';
import { FormDateField } from '../query-builder/hooks-form-querybuilder/form-date.field.component';
import { divisionApiKeyStore } from '../../api/masters/division-api-key-store';
import { DateFormat } from '../../components/hook-form/form-date.field.component';
import { useFormContext } from 'react-hook-form';
import dayjs from 'dayjs';
import { useEffect } from 'react';

type InspectionsFilterState = {
  customer: number | string;
  officeIDs: number[];
  projectIDs: number[];
  marketAreaIDs: number[];
  userIDs: number[];
  workOrderStatusID: number[];
  fromDate: string;
  toDate: string;
  divisions: number[];
  workOrderNumber: string;
};

interface InspectionsFiltersProps {
  inspectionsFilterState: InspectionsFilterState;
  setInspectionsFilterState: React.Dispatch<React.SetStateAction<InspectionsFilterState>>;
  handleInspectionsFilterClick: () => void;
}

const TIME_PERIOD = [
  { label: 'Today', value: 'today' },
  { label: 'Tomorrow', value: 'tomorrow' },
];

const InspectionsFilters = ({
  inspectionsFilterState,
  setInspectionsFilterState,
  handleInspectionsFilterClick,
}: InspectionsFiltersProps) => {
  const { setValue, watch } = useFormContext();

  const handleOnChange = (type: keyof InspectionsFilterState, value: any) => {
    setInspectionsFilterState((prev) => {
      const updatedState = { ...prev, [type]: value };
      if (type === 'customer') {
        updatedState.officeIDs = [];
        updatedState.projectIDs = [];
        setValue('officeIDs', []);
        setValue('projectIDs', []);
      }

      if (type === 'officeIDs') {
        updatedState.projectIDs = [];
        setValue('projectIDs', []);
      }

      return updatedState;
    });
  };

  useEffect(() => {
    setValue('timePeriod', [{ label: 'Today', value: 'today' }]);
    handleTimePeriodChange([{ label: 'Today', value: 'today' }]);
  }, []);

  const handleFilterClick = () => {
    if (!Array.isArray(watch('timePeriod')) || watch('timePeriod')?.length === 0) {
      setValue('timePeriod', [{ label: 'Today', value: 'today' }]);
    }
    handleInspectionsFilterClick();
  };

  const HOLIDAYS: string[] = []; // Add more holidays in MM/DD/YYYY format

  const isBusinessDay = (date: dayjs.Dayjs) => {
    const day = date.day(); // 0 (Sunday) - 6 (Saturday)
    const isWeekend = day === 0 || day === 6;
    const isHoliday = HOLIDAYS.includes(date.format('MM/DD/YYYY'));
    return !isWeekend && !isHoliday;
  };

  const getNextBusinessDay = (startDate: dayjs.Dayjs) => {
    let nextDate = startDate.add(1, 'day');
    while (!isBusinessDay(nextDate)) {
      nextDate = nextDate.add(1, 'day');
    }
    return nextDate;
  };

  const handleTimePeriodChange = (selectedValues: { label: string; value: string }[]) => {
    if (selectedValues?.length === 0) {
      handleTimePeriodChange([{ label: 'Today', value: 'today' }]);
    } else {
      const hasToday = selectedValues.some((item) => item.value === 'today');
      const hasTomorrow = selectedValues.some((item) => item.value === 'tomorrow');

      let fromDate: string | null = null;
      let toDate: string | null = null;

      const today = dayjs();

      if (hasToday && hasTomorrow) {
        const from = isBusinessDay(today) ? today : getNextBusinessDay(today);
        const to = getNextBusinessDay(from);
        fromDate = from.format('MM/DD/YYYY');
        toDate = to.format('MM/DD/YYYY');
      } else if (hasToday) {
        const from = isBusinessDay(today) ? today : getNextBusinessDay(today);
        fromDate = from.format('MM/DD/YYYY');
        toDate = fromDate;
      } else if (hasTomorrow) {
        const to = getNextBusinessDay(today);
        fromDate = to.format('MM/DD/YYYY');
        toDate = fromDate;
      }

      handleOnChange('fromDate', fromDate);
      handleOnChange('toDate', toDate);
    }
  };

  return (
    <>
      <Grid item lg={3} sm={6} xl={3} xs={6}>
        <FormAutoCompleteSelectDynamic
          name="customer"
          label="Customer"
          getQueryKeyConfig={() => customersApiKeyStore.getCustomers()}
          optionsValueKey="customerID"
          optionsLabelKey="customerName"
          onChange={(value: any) => handleOnChange('customer', value)}
        />
      </Grid>
      <Grid item lg={3} sm={6} xl={3} xs={6}>
        <FormAutoCompleteMultiSelectDynamic
          name="officeIDs"
          label="Offices"
          watchKeys={['customer']}
          getQueryKeyConfig={(inputData: any) => officeApiKeyStore.getCustomerOffices(inputData.customer?.customerID)}
          optionsLabelKey="officeName"
          optionsValueKey="officeID"
          disabled={!inspectionsFilterState.customer}
          onChange={
            (values: any) => handleOnChange('officeIDs', values)
            // setOfficeIDs(values)
          }
        />
      </Grid>
      <Grid item lg={3} sm={6} xl={3} xs={6}>
        <FormAutoCompleteMultiSelectDynamic
          name="projectIDs"
          label="Projects"
          watchKeys={['officeIDs']}
          getQueryKeyConfig={(inputData: any) =>
            projectsApiKeyStore.getOfficesProjects(inputData.officeIDs?.map((o: any) => o.officeID))
          }
          optionsLabelKey="projectName"
          optionsValueKey="projectID"
          disabled={!inspectionsFilterState.officeIDs || inspectionsFilterState.officeIDs?.length === 0}
          onChange={
            (values: any) => handleOnChange('projectIDs', values)
            //  setProjectIDs(values)
          }
        />
      </Grid>
      <Grid item lg={3} sm={6} xl={3} xs={6}>
        <FormAutoCompleteMultiSelectDynamic
          name="marketAreaIDs"
          label="Market Areas"
          getQueryKeyConfig={() => marketAreaApiKeyStore.getMarketAreas()}
          optionsLabelKey="marketAreaName"
          optionsValueKey="marketAreaID"
          onChange={(values: any) => handleOnChange('marketAreaIDs', values)}
          //   onChange={(values: any) => setMarketAreaIDs(values)}
        />
      </Grid>
      <Grid item lg={3} sm={6} xl={3} xs={6}>
        <FormAutoCompleteMultiSelectDynamic
          name="userIDs"
          label="Inspectors"
          getQueryKeyConfig={() => usersApiKeyStore.getUsersByRole('ins')}
          optionsLabelKey="fullName"
          optionsValueKey="userID"
          onChange={(values: any) => handleOnChange('userIDs', values)}
        />
      </Grid>
      {/* <Grid item lg={3} sm={6} xl={3} xs={6}>
        <FormAutoCompleteSelectDynamic
          name="workOrderStatusID"
          label="Status"
          getQueryKeyConfig={() => workOrdersApiKeyStore.getScheduleBalancingStatus()}
          optionsValueKey="workOrderStatusID"
          optionsLabelKey="workOrderStatus"
          onChange={(values: any) => handleOnChange('workOrderStatusID', values)}
          clearIcon={false}
        />
      </Grid> */}
      <Grid item lg={3} sm={6} xl={3} xs={6}>
        <FormAutoCompleteMultiSelectDynamic
          name="workOrderStatusID"
          label="Status"
          getQueryKeyConfig={() => workOrdersApiKeyStore.getScheduleBalancingStatus()}
          optionsValueKey="workOrderStatusID"
          optionsLabelKey="workOrderStatus"
          onChange={(values: any) => handleOnChange('workOrderStatusID', values)}
        />
      </Grid>
      <Grid item lg={3} sm={6} xl={3} xs={6}>
        <FormAutoCompleteMultiSelectDynamic
          name="timePeriod"
          label="Time Period"
          dataItems={TIME_PERIOD}
          optionsLabelKey="label"
          optionsValueKey="value"
          onChange={(values: any) => handleTimePeriodChange(values)}
        />
      </Grid>
      {/* <Grid item lg={2} sm={4} xl={2} xs={6}>
        <FormDateField
          onChange={(value) => handleOnChange('fromDate', value)}
          label="From"
          name="fromDate"
          format={DateFormat.SIMPLE_DATE}
        />
      </Grid>
      <Grid item lg={2} sm={4} xl={2} xs={6}>
        <FormDateField
          onChange={(value) => handleOnChange('toDate', value)}
          label="To"
          name="toDate"
          format={DateFormat.SIMPLE_DATE}
        />
      </Grid> */}
      <Grid item lg={3} sm={6} xl={3} xs={6}>
        <FormAutoCompleteMultiSelectDynamic
          name="divisions"
          label="Divisions"
          getQueryKeyConfig={() => divisionApiKeyStore.getDivisions()}
          optionsLabelKey="divisionName"
          optionsValueKey="divisionID"
          onChange={(values: any) => handleOnChange('divisions', values)}
        />
      </Grid>
      <Grid item lg={3} sm={6} xl={3} xs={6}>
        <TextField
          size="small"
          id="outlined-basic"
          placeholder="Work Order#"
          variant="outlined"
          onChange={(e: any) => handleOnChange('workOrderNumber', e.target.value)}
        />
      </Grid>
      <Grid item lg={2} sm={4} xl={2} xs={6}>
        <Button
          type="button"
          onClick={handleFilterClick}
          title="Filter"
          className="btn btn-primary "
          style={{ lineHeight: 1.6 }}
        >
          Filter
        </Button>
      </Grid>
    </>
  );
};

export default InspectionsFilters;
